import React, { ReactElement, useEffect, useState } from "react";
import styles from "./ValidatorsPage.module.scss";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import { Validator, ValidatorsInfo } from "../../models/Validator";
import { NodesList, Node, RelatedNode } from "../../models/Node";
import { freeSpaceOptions } from "../../components/validatorsFilter/ValidatorsFilter";
import {
  ActionIcon,
  Button,
  Combobox,
  Flex,
  Group,
  Input,
  Text,
  Title,
  useCombobox,
} from "@mantine/core";
import {
  fivePercentOfGwei,
  formatNumber,
  parseLocaleNumber,
} from "../../utils/formatter";
import { calculateDateDifference } from "../../components/transactionDetailsTable/TransactionDetailsTable.component";
import { CLOSE_ICON_URL } from "../../constants";
declare const window: any;

export function ValidatorsFilterMobilePage({
  validators,
  setValidators,
  applyFilters,
  outerDays,
  outerFreeSpace,
  closeFilter,
}: {
  validators: Validator[] | null;
  setValidators: (validators: Validator[] | null) => void;
  applyFilters: any;
  outerDays: number;
  outerFreeSpace: number;
  closeFilter: any;
}): ReactElement | null {
  const [filteredValidators, setFilteredValidators] = useState<
    Validator[] | null
  >(null);
  const [days, setDays] = useState<number>(outerDays);
  const [freeSpace, setFreeSpace] = useState<number>(outerFreeSpace);
  const [switchActive, setSwitchActive] = useState<boolean>(true);

  const combobox = useCombobox();

  const options = freeSpaceOptions.map((item) => (
    <Combobox.Option value={item.label} key={item.label}>
      {item.label}
    </Combobox.Option>
  ));

  const { data: validatorsInfo } = useQuery({
    queryKey: [QueryKey.validatorList],
    queryFn: (): Promise<ValidatorsInfo> => IndexerService.getValidatorList(),
  });

  const { data: nodesList } = useQuery({
    queryKey: [QueryKey.nodes],
    queryFn: (): Promise<NodesList> => IndexerService.getNodes(),
  });

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: "Validators",
        page_location: window.location.href,
      });
    }
  }, []);

  function enrichValidatorsData(
    validators: Validator[],
    nodes: Node[],
    capLimitBase: number | null
  ): void {
    const enrichedValidators = validators.map((validator: Validator) => {
      const node = nodes.find((n: Node) => n.nodeId === validator.nodeID);

      let totalFtsoStake = validator.totalStake;
      if (!node) {
        validator.capLimit =
          capLimitBase === null ? undefined : capLimitBase - totalFtsoStake;
        return validator;
      }

      node.relatedNodes.forEach((relatedNode: RelatedNode) => {
        const relatedValidator = validators.find(
          (val: Validator) => val.nodeID === relatedNode.nodeId
        );
        if (relatedValidator) {
          totalFtsoStake += relatedValidator.totalStake;
        }
      });

      if (node) {
        validator.roi = node.roi;
        validator.name = node.name;
        validator.ftsoAddress = node.ftsoAddress;
        validator.capLimit =
          capLimitBase === null ? undefined : capLimitBase - totalFtsoStake;
      }
      return validator;
    });
    setValidators(enrichedValidators);
    setFilteredValidators(enrichedValidators);
  }

  function filterValidators(
    days: number,
    freeSpace: number,
    validators: Validator[] | null
  ): Validator[] | null {
    if (!validators) {
      return null;
    }

    return validators.filter((validator: Validator) => {
      const daysRemaining = Math.floor(
        calculateDateDifference(new Date(), new Date(validator.endTime)) /
          (1000 * 60 * 60 * 24)
      );
      return validator.freeSpace / 1e9 >= freeSpace && daysRemaining >= days;
    });
  }

  useEffect(() => {
    if (validatorsInfo && nodesList) {
      enrichValidatorsData(
        validatorsInfo.validators,
        nodesList.nodes,
        fivePercentOfGwei(validatorsInfo.total)
      );
    }
  }, [validatorsInfo, nodesList]);

  return (
    <Flex className={styles.txContainer} direction={"column"} h={"100%"}>
      <ActionIcon className={styles.closeIconWrapper} variant={"subtle"}>
        <img src={CLOSE_ICON_URL} alt={"close icon"} onClick={closeFilter} />
      </ActionIcon>

      <Flex direction={"column"} p={30} h={"100%"}>
        <Flex direction={"column"} justify={"flex-end"} w={"100%"}>
          <Title
            w={"70%"}
            order={2}
            opacity={0.6}
            textWrap={"balance"}
            mt={30}
            mb={70}
            mr={0}
            className={styles.title}
          >
            Which stakeable validators do you want to see?
          </Title>
        </Flex>
        <Group className={styles.filterDropdowns} gap={10} w={"100%"}>
          <Text className={styles.text}>Show those with</Text>
          <Input
            defaultValue={outerDays}
            type={"number"}
            w={51}
            className={`${styles.number} ${styles.customInput} ${!switchActive ? styles.disabled : ""}`}
            onChange={(val) => {
              setDays(+val.target.value);
            }}
          />
          <Text className={styles.text}>days remaining and</Text>
          <Combobox
            onOptionSubmit={(optionValue) => {
              const selection = freeSpaceOptions.find(
                (el) => el.label === optionValue
              );
              if (selection) {
                setFreeSpace(selection.value);
              }

              combobox.closeDropdown();
            }}
            store={combobox}
          >
            <Combobox.Target>
              <Input
                className={`${styles.select} ${styles.customInput} ${!switchActive ? styles.disabled : ""}`}
                type={"text"}
                w={116}
                value={"" + formatNumber(freeSpace, false, 0)}
                onChange={(event) => {
                  setFreeSpace(
                    event.currentTarget.value ||
                      event.currentTarget.value.toLowerCase().includes("na")
                      ? parseLocaleNumber(event.currentTarget.value)
                      : 0
                  );

                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                }}
                onClick={() => combobox.openDropdown()}
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
              />
            </Combobox.Target>

            <Combobox.Dropdown className={styles.dropdown}>
              <Combobox.Options>
                {options.length === 0 ? (
                  <Combobox.Empty>Nothing found</Combobox.Empty>
                ) : (
                  options
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          <Text className={styles.text}>free space or more.</Text>
        </Group>
        <Button
          className={styles.confirmBtn}
          mt={"auto"}
          mb={0}
          w={"100%"}
          onClick={() => {
            setFilteredValidators(
              filterValidators(days, freeSpace, validators)
            );
            applyFilters(
              filteredValidators,
              switchActive,
              days,
              freeSpace,
              true
            );
          }}
        >
          Make it so!
        </Button>
      </Flex>
    </Flex>
  );
}
